import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch, API_BASE_URL } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, ui, fmtDate,
} from '../components/adminUi';

// Pending providers are read from the real users endpoint filtered by
// role=provider & moderationStatus=pending (the moderation flag lives on the
// user record). Approve/reject hit the canonical provider moderation paths.
export default function ModerationPage() {
  const { auth } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [detail, setDetail] = useState(null); // full user profile
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null); // provider being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiFetch(
        '/api/admin/users?role=provider&moderationStatus=pending&page=1',
        auth.token
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      const list = Array.isArray(data.items) ? data.items : [];
      setItems(list.filter((u) => (u.moderationStatus || '') === 'pending'));
    } catch {
      setItems([]);
      setFetchError('Ustalar ro\'yxatini yuklashda xatolik. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setActionMsg('');
    setDetail({ id, __loading: true });
    setDetailLoading(true);
    try {
      const res = await apiFetch(`/api/admin/users/${id}`, auth.token);
      if (!res.ok) throw new Error();
      setDetail(await res.json());
    } catch {
      setDetail({ id, __error: true });
    } finally {
      setDetailLoading(false);
    }
  };

  const doAction = async (providerId, action, body) => {
    setActing(true);
    setActionMsg('');
    try {
      const res = await apiFetch(`/api/admin/providers/${providerId}/${action}`, auth.token, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) {
        setDetail(null);
        setRejectFor(null);
        setRejectReason('');
        await load();
        return;
      }
      if (res.status === 404) {
        setActionMsg('Moderatsiya endpointi backendda hali ulanmagan (404). Frontend tayyor — ' +
          `POST /api/admin/providers/${providerId}/${action}.`);
        return;
      }
      const d = await res.json().catch(() => ({}));
      setActionMsg(d.message || 'Amalni bajarishda xatolik yuz berdi.');
    } catch {
      setActionMsg('Tarmoq xatosi. Qayta urinib ko\'ring.');
    } finally {
      setActing(false);
    }
  };

  const documents = (u) => {
    // Collect any document-like URLs the backend may expose.
    const urls = [];
    if (Array.isArray(u.documents)) urls.push(...u.documents);
    if (Array.isArray(u.documentUrls)) urls.push(...u.documentUrls);
    ['passportUrl', 'licenseUrl', 'certificateUrl'].forEach((k) => {
      if (u[k]) urls.push(u[k]);
    });
    return urls.filter(Boolean).map(String).map(httpsUrl);
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Ustalar moderatsiyasi"
        subtitle="Tasdiqlashni kutayotgan ustalar (moderationStatus = pending)"
      >
        <button style={{ ...ui.pagerBtn }} onClick={load}>↻ Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty icon="🛡️" title="Tasdiqlashni kutayotgan usta yo'q" hint="Hamma arizalar ko'rib chiqilgan." />
      ) : (
        <div style={ui.tableWrap}>
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={{ ...ui.th, width: '48px' }}>#</th>
                <th style={ui.th}>F.I.O</th>
                <th style={ui.th}>Telefon</th>
                <th style={ui.th}>MyID</th>
                <th style={ui.th}>Ariza sanasi</th>
                <th style={{ ...ui.th, textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u, i) => (
                <tr key={u.id} style={ui.tr}>
                  <td style={{ ...ui.td, color: 'var(--text-faint)' }}>{i + 1}</td>
                  <td style={{ ...ui.td, fontWeight: '600', color: 'var(--text)' }}>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td style={ui.td}>{u.phone || '—'}</td>
                  <td style={ui.td}>
                    {u.myidVerified
                      ? <Badge tone="green">Tasdiqlangan</Badge>
                      : <Badge tone="gray">Tasdiqlanmagan</Badge>}
                  </td>
                  <td style={ui.td}>{fmtDate(u.createdAt)}</td>
                  <td style={{ ...ui.td, textAlign: 'right' }}>
                    <div style={ui.actions}>
                      <button style={{ ...ui.btnSm, ...ui.btnBlue }} onClick={() => openDetail(u.id)}>
                        Ko'rish
                      </button>
                      <button
                        style={{ ...ui.btnSm, ...ui.btnGreen }}
                        onClick={() => doAction(u.id, 'approve')}
                        disabled={acting}
                      >
                        Tasdiqlash
                      </button>
                      <button
                        style={{ ...ui.btnSm, ...ui.btnRed }}
                        onClick={() => { setRejectFor(u); setRejectReason(''); setActionMsg(''); }}
                        disabled={acting}
                      >
                        Rad etish
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <Modal title="Usta ma'lumotlari" onClose={() => setDetail(null)} maxWidth="480px">
          {detailLoading || detail.__loading ? (
            <Loading />
          ) : detail.__error ? (
            <ErrorBanner>Ma'lumotni yuklab bo'lmadi.</ErrorBanner>
          ) : (
            <>
              <div style={d.grid}>
                <Info label="F.I.O" value={[detail.firstName, detail.lastName].filter(Boolean).join(' ')} />
                <Info label="Telefon" value={detail.phone} />
                <Info label="Holat" value={detail.status} />
                <Info label="Moderatsiya" value={detail.moderationStatus} />
                <Info label="MyID" value={detail.myidVerified ? 'Tasdiqlangan' : 'Tasdiqlanmagan'} />
                <Info label="Ariza sanasi" value={fmtDate(detail.createdAt, true)} />
              </div>

              <div style={d.docsBlock}>
                <div style={ui.label}>Hujjatlar</div>
                {documents(detail).length === 0 ? (
                  <div style={d.noDocs}>Hujjat yuklanmagan</div>
                ) : (
                  <div style={d.docsGrid}>
                    {documents(detail).map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" style={d.docLink}>
                        <img src={url} alt={`hujjat ${i + 1}`} style={d.docImg} />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {actionMsg && <div style={ui.infoBanner}>{actionMsg}</div>}

              <div style={ui.modalActions}>
                <button
                  style={{ ...ui.cancelBtn }}
                  onClick={() => { setRejectFor(detail); setRejectReason(''); }}
                  disabled={acting}
                >
                  Rad etish
                </button>
                <button
                  style={{ ...ui.saveBtn, background: 'var(--success)', ...(acting ? ui.btnDisabled : {}) }}
                  onClick={() => doAction(detail.id, 'approve')}
                  disabled={acting}
                >
                  {acting ? 'Bajarilmoqda...' : 'Tasdiqlash'}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Reject modal with reason */}
      {rejectFor && (
        <Modal title="Rad etish sababi" onClose={() => setRejectFor(null)} maxWidth="420px">
          <div style={ui.field}>
            <label style={ui.label}>Sabab <span style={ui.req}>*</span></label>
            <textarea
              style={ui.textarea}
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Rad etish sababini yozing"
              disabled={acting}
              autoFocus
            />
          </div>
          {actionMsg && <div style={ui.infoBanner}>{actionMsg}</div>}
          <div style={ui.modalActions}>
            <button style={ui.cancelBtn} onClick={() => setRejectFor(null)} disabled={acting}>
              Bekor qilish
            </button>
            <button
              style={{ ...ui.saveBtn, background: 'var(--danger)', ...(acting || !rejectReason.trim() ? ui.btnDisabled : {}) }}
              onClick={() => doAction(rejectFor.id, 'reject', { reason: rejectReason.trim() })}
              disabled={acting || !rejectReason.trim()}
            >
              {acting ? 'Bajarilmoqda...' : 'Rad etish'}
            </button>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={d.infoLabel}>{label}</div>
      <div style={d.infoValue}>{value === null || value === undefined || value === '' ? '—' : value}</div>
    </div>
  );
}

// Force absolute https so document images load reliably.
function httpsUrl(u) {
  if (!u) return u;
  if (u.startsWith('http://')) return 'https://' + u.slice(7);
  if (u.startsWith('/')) return API_BASE_URL + u;
  return u;
}

const d = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px 20px',
    marginBottom: '18px',
  },
  infoLabel: { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '2px' },
  infoValue: { fontSize: '14px', color: 'var(--text)', fontWeight: '500' },
  docsBlock: { marginBottom: '16px' },
  noDocs: {
    fontSize: '13px',
    color: 'var(--text-faint)',
    background: 'var(--bg)',
    border: '1px dashed #e5e7eb',
    borderRadius: '8px',
    padding: '14px',
    textAlign: 'center',
  },
  docsGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  docLink: { display: 'block' },
  docImg: {
    width: '90px',
    height: '90px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
};
